import { v4 as uuid } from 'uuid';

import { SporsmalType, VedleggId } from '../../types/generated-types';
import { CurrentSporsmal } from '../reducer/current-steg';
import { Filopplastingsporsmal } from '../reducer/sporsmalsflyt-types';
import { isCurrentSporsmalSide } from '../sporsmalSide/sporsmalSide-types';

export interface UploadedVedleggResult {
    vedlegg: VedleggWithFile[];
}

export interface VedleggIdInternal {
    id?: string;
    fileName: string;
}

export interface UploadedVedlegg {
    vedleggIds: VedleggIdInternal[];
}

export interface VedleggWithFile extends VedleggIdInternal {
    uploadId: string;
    fil: File;
    url: string;
    size?: number;
    status: VedleggStatus;
}

export interface Vedlegg extends VedleggIdInternal {
    uploadId: string;
    fileInfo: FileInfo;
    url: string;
    size?: number;
    status: VedleggStatus;
}

export interface FileInfo {
    name: string;
    size: number;
    type: string;
}

export enum VedleggStatus {
    Uploading = 'Uploading',
    Ok = 'Ok',
    Error = 'Error',
    ErrorTotalFileSize = 'ErrorTotalFileSize',
}

export enum VedleggResultStatus {
    Uploading = 'Uploading',
    Ok = 'Ok',
    Error = 'Error',
}

export interface CurrentFilopplastingsporsmal {
    type: SporsmalType.Filopplasting;
    sporsmal: Filopplastingsporsmal;
    filer: Vedlegg[];
    status?: VedleggResultStatus;
}

export interface BesvartFilopplastingsporsmal {
    type: SporsmalType.Filopplasting;
    sporsmal: Filopplastingsporsmal;
    filer: Vedlegg[];
    ids: VedleggIdInternal[];
}

export const isFilopplastingsporsmal = (
    currentSporsmal: CurrentSporsmal,
): currentSporsmal is CurrentFilopplastingsporsmal => currentSporsmal.type === SporsmalType.Filopplasting;

export const updateVedlegg = (
    currentSporsmal: CurrentSporsmal,
    result: UploadedVedleggResult,
    status: VedleggResultStatus,
): CurrentSporsmal => {
    const isFilopplasting = isFilopplastingsporsmal(currentSporsmal);

    if (isFilopplasting) {
        return {
            ...currentSporsmal,
            filer: currentSporsmal.filer.map((currentVedlegg: Vedlegg) => {
                const uploadedVedlegg = result.vedlegg.find(
                    (uploadedVedlegg: VedleggWithFile) => uploadedVedlegg.uploadId === currentVedlegg.uploadId,
                );

                if (!uploadedVedlegg) {
                    return { ...currentVedlegg };
                }

                return {
                    ...currentVedlegg,
                    id: uploadedVedlegg.id,
                    status: uploadedVedlegg.status,
                };
            }),
            status,
        };
    }

    return currentSporsmal;
};

export const vedleggWithFileToVedlegg = (vedlegg: VedleggWithFile[]): Vedlegg[] =>
    vedlegg.map((vedlegg) => ({
        id: vedlegg.id,
        fileName: vedlegg.fileName,
        uploadId: vedlegg.uploadId,
        fileInfo: {
            size: vedlegg.fil.size,
            name: vedlegg.fil.name,
            type: vedlegg.fil.type,
        },
        url: vedlegg.url,
        size: vedlegg.size,
        status: vedlegg.status,
    }));

export const addVedleggToCurrent = (currentSporsmal: CurrentSporsmal, vedlegg: Vedlegg[]): CurrentSporsmal => {
    if (isFilopplastingsporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            filer: currentSporsmal.filer.concat(vedlegg),
        };
    }
    return currentSporsmal;
};

export const deleteVedleggFromCurrent = (
    currentSporsmal: CurrentSporsmal,
    vedleggToBeDeleted: Vedlegg[],
): CurrentSporsmal => {
    if (isFilopplastingsporsmal(currentSporsmal)) {
        if (!vedleggToBeDeleted.length) {
            return { ...currentSporsmal };
        }

        return {
            ...currentSporsmal,
            filer: currentSporsmal.filer.filter((fil) => vedleggToBeDeleted.some((v) => v.id !== fil.id)),
        };
    }
    return currentSporsmal;
};

export const fileToVedlegg = (files: File[]) => {
    return files.map((file: File) => ({
        id: null,
        uploadId: uuid(),
        fil: file,
        url: URL.createObjectURL(file),
        fileName: file.name,
        size: file.size,
        status: VedleggStatus.Uploading,
    }));
};

export const directUploadingFile = (currentSporsmal: CurrentSporsmal): boolean => {
    if (isCurrentSporsmalSide(currentSporsmal)) {
        const currentSporsmalSideFilopplastingsporsmal =
            currentSporsmal.sporsmalsflytCollection.currentSporsmalList.find(isFilopplastingsporsmal);
        return currentSporsmalSideFilopplastingsporsmal && currentSporsmalSideFilopplastingsporsmal.status
            ? currentSporsmalSideFilopplastingsporsmal.status === VedleggResultStatus.Uploading
            : false;
    }

    return isFilopplastingsporsmal(currentSporsmal) && currentSporsmal.status
        ? currentSporsmal.status === VedleggResultStatus.Uploading
        : false;
};

export const addDirectVedleggToCurrent = (
    currentSporsmal: CurrentSporsmal,
    vedlegg: Vedlegg[],
    status: VedleggResultStatus,
): CurrentSporsmal => {
    if (isFilopplastingsporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            filer: [...currentSporsmal.filer, ...vedlegg],
            status,
        };
    }
    return currentSporsmal;
};

export const toUploadedVedleggId = (vedlegg: UploadedVedlegg): VedleggId[] => {
    if (vedlegg === undefined) return [];
    return vedlegg.vedleggIds.map((vedleggId) => vedleggId as VedleggId);
};

export const toUploadedVedleggDTO = (vedlegg: UploadedVedlegg) => {
    return { vedleggIds: toUploadedVedleggId(vedlegg) };
};
